import { create } from 'zustand';
import { ServiceRequest, RequestState, PaginatedResponse } from '../types';
import { requestService } from '../services/requestService';

interface RequestStore {
  requests: ServiceRequest[];
  currentRequest: ServiceRequest | null;
  isLoading: boolean;
  error: string | null;
  pagination: { count: number; next: string | null; previous: string | null };

  fetchRequests: (params?: { status?: RequestState; page?: number; search?: string }) => Promise<void>;
  fetchRequestDetail: (id: string) => Promise<void>;
  createRequest: (payload: any) => Promise<ServiceRequest>;
  updateRequestInList: (updated: ServiceRequest) => void;
  clearError: () => void;
}

export const useRequestStore = create<RequestStore>((set, get) => ({
  requests: [],
  currentRequest: null,
  isLoading: false,
  error: null,
  pagination: { count: 0, next: null, previous: null },

  fetchRequests: async (params) => {
    try {
      set({ isLoading: true, error: null });
      const data = await requestService.list(params);
      set({
        requests: data.results ?? [],
        pagination: { count: data.count, next: data.next, previous: data.previous },
      });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to fetch requests' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchRequestDetail: async (id: string) => {
    try {
      set({ isLoading: true, error: null });
      const data = await requestService.getDetail(id);
      set({ currentRequest: data });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to fetch request' });
    } finally {
      set({ isLoading: false });
    }
  },

  createRequest: async (payload) => {
    try {
      set({ isLoading: true, error: null });
      const created = await requestService.create(payload);
      set((state) => ({ requests: [created, ...state.requests] }));
      return created;
    } catch (err: any) {
      set({ error: err?.message || 'Failed to create request' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateRequestInList: (updated: ServiceRequest) => {
    set((state) => ({
      requests: state.requests.map((r) => (r.id === updated.id ? updated : r)),
      currentRequest:
        state.currentRequest?.id === updated.id ? updated : state.currentRequest,
    }));
  },

  clearError: () => set({ error: null }),
}));
